import React, { useState } from 'react';
import { getDeviceComplianceDetail } from '../../api/internal/usersInternalApi';
import {
    Card, CardTitle, Button, Pill, PillDot, Notice, EmptyState, Spinner,
} from '../ui';
import { PolicyBlock, PolicyHeader, SettingRow, StateCell, Muted } from './security.styled';
import { formatDate } from './severity';

const STATE_TONE = {
    compliant: 'ok',
    nonCompliant: 'danger',
    error: 'danger',
    conflict: 'warn',
    inGracePeriod: 'warn',
    notApplicable: 'neutral',
};

const STATE_LABEL = {
    compliant: 'Conforme',
    nonCompliant: 'Non conforme',
    error: 'Erreur',
    conflict: 'Conflit',
    inGracePeriod: 'Période de grâce',
    notApplicable: 'Non applicable',
    unknown: 'Inconnu',
};

function StatePill({ state }) {
    return (
        <Pill $tone={STATE_TONE[state] || 'neutral'}>
            <PillDot />
            {STATE_LABEL[state] || state || 'Inconnu'}
        </Pill>
    );
}

// Convertit la réponse de l'appel Graph direct dans la forme servie par
// /api/device-overview/:key, pour que le rendu ci-dessous soit unique — deux
// gabarits pour la même information finiraient par diverger.
const FAILING_STATES = ['nonCompliant', 'error', 'conflict'];

function liveToCompliance(live) {
    const s = live.summary || {};
    // Le décompte par paramètre n'existe pas dans la réponse de l'appel direct :
    // on le recalcule ici pour que les deux sources se comportent pareil.
    const policies = (live.policies || []).map((p) => ({
        ...p,
        failingCount: (p.settings || []).filter((x) => FAILING_STATES.includes(x.state)).length,
    }));
    return {
        managedByIntune: !live.notManagedByIntune,
        total: s.total ?? 0,
        compliant: s.compliant ?? 0,
        nonCompliant: s.nonCompliant ?? 0,
        notApplicable: s.notApplicable ?? 0,
        disagreeing: s.disagreeing ?? 0,
        // Interrogation directe : rien ne peut être « pas encore collecté ». En
        // revanche Graph peut refuser des appels, d'où `unavailable`.
        neverSynced: 0,
        unavailable: s.unavailable ?? 0,
        failingSettings: policies.reduce((n, p) => n + p.failingCount, 0),
        lastSync: new Date().toISOString(),
        live: true,
        policies,
    };
}

// Détail de conformité Intune d'un appareil : une stratégie par bloc dépliable,
// avec les paramètres réellement évalués et la valeur relevée sur le poste.
//
// À l'ouverture, la donnée arrive dans la réponse /api/device-overview/:key :
// aucune requête propre, aucun quota Graph consommé. L'appel direct existe
// toujours mais derrière un bouton — il coûte un appel PAR STRATÉGIE sur
// l'endpoint le plus étranglé de Graph, ce qui ne se justifie que sur demande
// explicite, typiquement pour vérifier une remédiation qu'on vient d'appliquer.
export default function ComplianceDetail({ compliance, deviceName, deviceKey }) {
    const [open, setOpen] = useState({});
    const [live, setLive] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState(null);

    const refresh = () => {
        if (!deviceKey) return;
        setRefreshing(true);
        setRefreshError(null);
        getDeviceComplianceDetail(deviceKey)
            .then((result) => {
                if (result) setLive(liveToCompliance(result));
                else setRefreshError("Cet appareil n'est pas rapproché de l'inventaire Intune.");
            })
            .catch(() => setRefreshError('Interrogation d\'Intune impossible.'))
            .finally(() => setRefreshing(false));
    };

    if (!compliance) return null;

    // L'état affiché est le rafraîchissement s'il a eu lieu, la base sinon.
    const view = live || compliance;

    if (!compliance.managedByIntune) {
        return (
            <Card>
                <CardTitle>Conformité Intune</CardTitle>
                <Notice $tone="warn">
                    <i className="bx bx-error" />
                    {deviceName || 'Cet appareil'} n'est pas géré par Intune :
                    aucune stratégie de conformité ne lui est appliquée.
                </Notice>
            </Card>
        );
    }

    const policies = view.policies || [];

    return (
        <Card $pad="16px">
            <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                Conformité Intune
                <Pill $tone={view.nonCompliant > 0 ? 'danger' : 'ok'}>
                    {view.compliant}/{view.total} conformes
                </Pill>
                {view.notApplicable > 0 && (
                    <Pill $tone="neutral">{view.notApplicable} non applicables</Pill>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginLeft: 'auto' }}>
                    {/* La date n'est pas décorative : elle borne ce que l'utilisateur
                        peut conclure du détail affiché. */}
                    <Muted style={{ fontWeight: 400, fontSize: '0.72rem' }}>
                        {view.live
                            ? 'Interrogé à l\'instant auprès d\'Intune'
                            : view.lastSync
                                ? `Détail collecté le ${formatDate(view.lastSync)}`
                                : 'Détail jamais collecté'}
                    </Muted>
                    {deviceKey && (
                        <Button
                            $variant="secondary"
                            onClick={refresh}
                            disabled={refreshing}
                            title="Interroge Intune en direct : un appel par stratégie, à n'utiliser qu'au besoin"
                        >
                            {refreshing
                                ? <Spinner $size="14px" />
                                : <i className="bx bx-refresh" />}
                            {refreshing ? 'Interrogation...' : 'Rafraîchir'}
                        </Button>
                    )}
                </div>
            </CardTitle>

            {refreshError && (
                <Notice $tone="danger" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-error-circle" />
                    {refreshError} Le détail affiché reste celui de la dernière synchronisation.
                </Notice>
            )}

            {view.disagreeing > 0 && (
                <Notice $tone="warn" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-error" />
                    <span>
                        {view.disagreeing} stratégie{view.disagreeing > 1 ? 's sont' : ' est'} marquée
                        {view.disagreeing > 1 ? 's' : ''} en échec alors que tous
                        {view.disagreeing > 1 ? ' leurs' : ' ses'} paramètres sont conformes.
                        L'état agrégé se périme quand l'appareil n'a pas refait de check-in récent —
                        le détail ci-dessous est la source la plus fiable.
                    </span>
                </Notice>
            )}

            {view.neverSynced > 0 && (
                <Notice $tone="accent" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-info-circle" />
                    <span>
                        Le détail de {view.neverSynced} stratégie
                        {view.neverSynced > 1 ? 's' : ''} n'a pas encore été collecté.
                        La synchronisation de midi ne couvre que les stratégies en échec ;
                        les autres sont récupérées par le balayage complet de minuit.
                        Le bouton Rafraîchir interroge Intune directement.
                    </span>
                </Notice>
            )}

            {/* Uniquement après un rafraîchissement : Graph refuse régulièrement une
                partie des appels, et un détail manquant pour cette raison ne doit pas
                se confondre avec un détail simplement pas encore collecté. */}
            {view.unavailable > 0 && (
                <Notice $tone="warn" style={{ marginBottom: '12px' }}>
                    <i className="bx bx-error-circle" />
                    Le détail de {view.unavailable} stratégie{view.unavailable > 1 ? 's' : ''} n'a
                    pas pu être récupéré auprès de Graph lors de cette interrogation.
                </Notice>
            )}

            {policies.length === 0 ? (
                <EmptyState>
                    <i className="bx bx-shield" />
                    <h3>Aucune stratégie</h3>
                    <p>Aucune stratégie de conformité n'est appliquée à cet appareil.</p>
                </EmptyState>
            ) : (
                policies.map((policy) => {
                    // Un bloc portant des paramètres en échec s'ouvre de lui-même :
                    // replié sous un libellé « non applicable », personne ne va le
                    // dérouler, et c'est là que se cache la moitié des vrais échecs.
                    // `??` et non `||` : un clic pour refermer doit rester possible.
                    const isOpen = open[policy.policyId] ?? policy.failingCount > 0;
                    return (
                        <PolicyBlock key={policy.policyId} $state={policy.state}>
                            <PolicyHeader
                                type="button"
                                $open={isOpen}
                                onClick={() => setOpen((prev) => ({ ...prev, [policy.policyId]: !isOpen }))}
                            >
                                <StateCell><StatePill state={policy.state} /></StateCell>
                                <span className="name">{policy.displayName}</span>
                                {/* L'état du paramètre prime sur celui de la stratégie :
                                    ce badge apparaît quel que soit policyState. */}
                                {policy.failingCount > 0 && (
                                    <Pill $tone="danger">
                                        <PillDot />
                                        {policy.failingCount} en échec
                                    </Pill>
                                )}
                                {policy.settingsDisagreeWithPolicy && (
                                    <Pill $tone="warn">Détail contredit l'agrégat</Pill>
                                )}
                                {/* Stratégie présente dans le détail mais plus dans la
                                    liste de l'appareil : les deux ne sont pas collectés
                                    au même rythme, on le dit au lieu de la masquer. */}
                                {policy.notInPolicyList && (
                                    <Pill $tone="neutral">Hors liste courante</Pill>
                                )}
                                <Muted style={{ fontSize: '0.72rem' }}>
                                    {policy.settings === null
                                        ? (policy.fetchError ? 'indisponible' : 'non collecté')
                                        : `${policy.settings.length} paramètre${policy.settings.length > 1 ? 's' : ''}`}
                                </Muted>
                                <i className="bx bx-chevron-down" />
                            </PolicyHeader>

                            {/* Deux raisons distinctes à un détail absent : Graph a
                                refusé l'appel (rafraîchissement), ou la synchro ne l'a
                                pas encore ramassé. Les confondre ferait chercher une
                                panne là où il n'y a qu'un périmètre. */}
                            {isOpen && policy.settings === null && (
                                <SettingRow>
                                    <Muted>—</Muted>
                                    <span className="name">
                                        {policy.fetchError ? (
                                            <>
                                                Détail non récupéré auprès de Graph.
                                                <br /><Muted>{policy.fetchError}</Muted>
                                            </>
                                        ) : (
                                            <>
                                                Détail non collecté pour cette stratégie.
                                                <br />
                                                <Muted>
                                                    Elle n'est pas en échec : seul le balayage complet de
                                                    minuit récupère les paramètres des stratégies conformes.
                                                    Le bouton Rafraîchir l'obtient immédiatement.
                                                </Muted>
                                            </>
                                        )}
                                    </span>
                                    <span className="value" />
                                </SettingRow>
                            )}

                            {isOpen && policy.settings?.map((setting) => (
                                <SettingRow key={setting.setting || setting.settingName}>
                                    <StatePill state={setting.state} />
                                    <span className="name">
                                        {setting.settingName}
                                        {setting.errorDescription && (
                                            <><br /><Muted>{setting.errorDescription}</Muted></>
                                        )}
                                    </span>
                                    <span className="value">{setting.currentValue ?? ''}</span>
                                </SettingRow>
                            ))}
                        </PolicyBlock>
                    );
                })
            )}
        </Card>
    );
}
